/**
 * Chat IA — second conversational assistant. ChatGPT-style UI:
 * left rail = workspaces (with file uploads for RAG), middle rail =
 * conversation list, right pane = active conversation.
 *
 * Workspaces give the assistant a "memory" backed by uploaded
 * PDF/Word/TXT documents (extracted, chunked, embedded server-side).
 * Conversations bound to a workspace get top-K semantic-search hits
 * injected into the system prompt at every turn.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ChangeEvent,
} from "react";
import { useLocation, useParams } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  MessageSquare,
  Plus,
  Trash2,
  FolderPlus,
  Upload,
  FileText,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Send,
  Sparkles,
  X,
  Mail,
  TrendingDown,
  ListChecks,
  CalendarCheck,
  Lightbulb,
  PenLine,
  ArrowRight,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { MarkdownLite } from "@/components/markdown-lite";
import {
  listChatConversations,
  listChatWorkspaces,
  createChatConversation,
  createChatWorkspace,
  deleteChatConversation,
  deleteChatWorkspace,
  getChatConversation,
  listChatFiles,
  uploadChatFile,
  deleteChatFile,
  patchChatConversation,
  type ChatConversation,
  type ChatMessage,
  type ChatWorkspace,
  type ChatFile,
} from "@/lib/chat-api";
import { sendChatMessageStream } from "@/lib/chat-stream";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { getDemoChatConversations } from "@/lib/demo-chat-data";

const POLL_FILES_MS = 2500;

function formatRelative(iso: string, lang: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60000);
  if (lang === "en") {
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h}h ago`;
    const day = Math.round(h / 24);
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString("en-GB");
  }
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const day = Math.round(h / 24);
  if (day < 7) return `il y a ${day} j`;
  return d.toLocaleDateString("fr-FR");
}

function fileSizeLabel(n: number): string {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

// ============================================================
// Workspaces panel (left rail)
// ============================================================

function WorkspacesPanel({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { active: isDemo } = useDemoMode();
  const { data, isLoading } = useQuery({
    queryKey: ["chat-workspaces"],
    queryFn: () => listChatWorkspaces(),
    refetchOnWindowFocus: false,
    enabled: !isDemo,
  });
  const workspaces = isDemo ? [] : (data?.workspaces ?? []);

  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ChatWorkspace | null>(
    null,
  );

  const handleCreate = async () => {
    setCreating(true);
    try {
      const r = await createChatWorkspace(newName.trim() || t("chat_page.new_workspace_placeholder"));
      await qc.invalidateQueries({ queryKey: ["chat-workspaces"] });
      onSelect(r.workspace.id);
      setNewOpen(false);
      setNewName("");
    } catch (err) {
      toast({
        title: t("chat_page.create_error"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (ws: ChatWorkspace) => {
    try {
      await deleteChatWorkspace(ws.id);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["chat-workspaces"] }),
        qc.invalidateQueries({ queryKey: ["chat-conversations"] }),
      ]);
      if (selectedId === ws.id) onSelect(null);
      setConfirmDelete(null);
    } catch (err) {
      toast({
        title: t("chat_page.delete_error"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("chat_page.workspaces_label")}
        </h3>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              data-testid="button-new-workspace"
            >
              <FolderPlus className="w-4 h-4" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("chat_page.dialog_title")}</DialogTitle>
              <DialogDescription>
                {t("chat_page.dialog_desc")}
              </DialogDescription>
            </DialogHeader>
            <Input
              placeholder={t("chat_page.workspace_name_placeholder")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="input-workspace-name"
              autoFocus
            />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setNewOpen(false)}
                disabled={creating}
              >
                {t("chat_page.cancel_btn")}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating}
                data-testid="button-create-workspace"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t("chat_page.create_btn")
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-1">
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={`w-full text-left px-2.5 py-1.5 rounded-md text-sm transition-colors ${
            selectedId === null
              ? "bg-secondary text-foreground"
              : "text-muted-foreground hover:bg-secondary/50"
          }`}
          data-testid="workspace-none"
        >
          {t("chat_page.no_memory")}
        </button>
        {isLoading ? (
          <>
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-full" />
          </>
        ) : workspaces.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2.5 py-2">
            {t("chat_page.empty_workspaces")}
          </p>
        ) : (
          workspaces.map((w) => (
            <div
              key={w.id}
              className={`group flex items-center rounded-md ${
                selectedId === w.id
                  ? "bg-secondary"
                  : "hover:bg-secondary/50"
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(w.id)}
                className="flex-1 text-left px-2.5 py-1.5 text-sm truncate"
                data-testid={`workspace-${w.id}`}
              >
                {w.name}
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover:opacity-100 mr-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete(w);
                }}
                data-testid={`workspace-delete-${w.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))
        )}
      </div>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("chat_page.delete_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("chat_page.delete_dialog_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("chat_page.cancel_btn")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
              data-testid="button-confirm-delete-workspace"
            >
              {t("chat_page.delete_btn")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ============================================================
// Files panel for the active workspace
// ============================================================

function FilesPanel({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const { data } = useQuery({
    queryKey: ["chat-files", workspaceId],
    queryFn: () => listChatFiles(workspaceId),
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      const files = (q.state.data as { files: ChatFile[] } | undefined)
        ?.files ?? [];
      return files.some((f) => f.status === "processing")
        ? POLL_FILES_MS
        : false;
    },
  });
  const files = data?.files ?? [];

  const handleFiles = useCallback(
    async (filesToUpload: FileList | null) => {
      if (!filesToUpload || filesToUpload.length === 0) return;
      setUploading(true);
      try {
        for (const f of Array.from(filesToUpload)) {
          await uploadChatFile(workspaceId, f);
        }
        await qc.invalidateQueries({
          queryKey: ["chat-files", workspaceId],
        });
      } catch (err) {
        toast({
          title: t("chat_page.upload_error"),
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [workspaceId, qc, toast],
  );

  const handleDelete = async (id: string) => {
    try {
      await deleteChatFile(id);
      await qc.invalidateQueries({ queryKey: ["chat-files", workspaceId] });
    } catch (err) {
      toast({
        title: t("chat_page.delete_error"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("chat_page.docs_label")}
        </h3>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="button-upload-file"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
          className="hidden"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleFiles(e.target.files)
          }
          data-testid="input-file-upload"
        />
      </div>

      <div className="space-y-1">
        {files.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2.5 py-2">
            {t("chat_page.upload_hint")}
          </p>
        ) : (
          files.map((f) => (
            <div
              key={f.id}
              className="group flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-secondary/50"
              data-testid={`file-${f.id}`}
            >
              <FileText className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-xs truncate">{f.name}</p>
                <p className="text-[10px] text-muted-foreground">
                  {fileSizeLabel(f.sizeBytes)}
                  {f.status === "ready" &&
                    ` • ${t("chat_page.chunks", { count: f.chunkCount })}`}
                </p>
              </div>
              {f.status === "processing" && (
                <Loader2
                  className="w-3.5 h-3.5 animate-spin text-muted-foreground"
                  data-testid={`file-status-processing-${f.id}`}
                />
              )}
              {f.status === "ready" && (
                <CheckCircle2
                  className="w-3.5 h-3.5 text-blue-600"
                  data-testid={`file-status-ready-${f.id}`}
                />
              )}
              {f.status === "failed" && (
                <AlertCircle
                  className="w-3.5 h-3.5 text-destructive"
                  data-testid={`file-status-failed-${f.id}`}
                />
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100"
                onClick={() => handleDelete(f.id)}
                data-testid={`file-delete-${f.id}`}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================
// Conversations list (middle rail)
// ============================================================

function ConversationsPanel({
  selectedId,
  onSelect,
  workspaceFilter,
  defaultWorkspaceId,
}: {
  selectedId: string | null;
  onSelect: (id: string) => void;
  workspaceFilter: string | null;
  defaultWorkspaceId: string | null;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { active: isDemo } = useDemoMode();
  const demoConvs = useMemo(
    () => (isDemo ? (getDemoChatConversations(i18n.language) as ChatConversation[]) : []),
    [isDemo, i18n.language],
  );
  const { data, isLoading } = useQuery({
    queryKey: ["chat-conversations"],
    queryFn: () => listChatConversations(),
    refetchOnWindowFocus: false,
    enabled: !isDemo,
  });
  const conversations = isDemo ? demoConvs : (data?.conversations ?? []);
  const filtered = useMemo(() => {
    if (workspaceFilter === null) return conversations;
    if (workspaceFilter === "__none__")
      return conversations.filter((c) => c.workspaceId === null);
    return conversations.filter((c) => c.workspaceId === workspaceFilter);
  }, [conversations, workspaceFilter]);

  const handleNew = async () => {
    if (isDemo) {
      toast({ title: t("chat_page.demo_readonly") });
      return;
    }
    try {
      const r = await createChatConversation({
        workspaceId: defaultWorkspaceId ?? null,
      });
      await qc.invalidateQueries({ queryKey: ["chat-conversations"] });
      onSelect(r.conversation.id);
    } catch (err) {
      toast({
        title: t("chat_page.create_error"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (isDemo) return;
    try {
      await deleteChatConversation(id);
      await qc.invalidateQueries({ queryKey: ["chat-conversations"] });
      if (selectedId === id) onSelect("");
    } catch (err) {
      toast({
        title: t("chat_page.delete_error"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("chat_page.conversations_label")}
        </h3>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={handleNew}
          data-testid="button-new-conversation"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-1">
        {isLoading ? (
          <>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2.5 py-3">
            {t("chat_page.empty_conversations")}
          </p>
        ) : (
          filtered.map((c) => (
            <div
              key={c.id}
              className={`group flex items-center rounded-md ${
                selectedId === c.id ? "bg-secondary" : "hover:bg-secondary/50"
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(c.id)}
                className="flex-1 min-w-0 text-left px-2.5 py-1.5"
                data-testid={`conversation-${c.id}`}
              >
                <p className="text-sm truncate">{c.title}</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {formatRelative(c.lastMessageAt, i18n.language)}
                </p>
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover:opacity-100 mr-1 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDelete(c.id);
                }}
                data-testid={`conversation-delete-${c.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))
        )}
      </div>
    </>
  );
}

// ============================================================
// Active conversation pane
// ============================================================

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === "user";
  const sources =
    (msg.meta as { sources?: { fileName: string; chunkIndex: number }[] } | null)
      ?.sources ?? [];
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`chat-message-${msg.role}`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-secondary text-secondary-foreground"
        }`}
      >
        <MarkdownLite text={msg.content || "…"} />
        {!isUser && sources.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border/50 flex flex-wrap gap-1">
            {Array.from(new Set(sources.map((s) => s.fileName))).map((name) => (
              <Badge
                key={name}
                variant="outline"
                className="text-[10px] gap-1"
                data-testid="chat-source-badge"
              >
                <FileText className="w-3 h-3" />
                {name}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationView({
  conversationId,
  workspaces,
  onBack,
  initialDraft,
  onInitialDraftConsumed,
}: {
  conversationId: string;
  workspaces: ChatWorkspace[];
  /** When provided (mobile), shows a back button in the header. */
  onBack?: () => void;
  /**
   * Starter prompt to pre-fill the composer with on first mount of a freshly
   * created conversation. Consumed once and immediately cleared by the parent
   * via `onInitialDraftConsumed` so it doesn't re-apply if the user empties
   * the composer.
   */
  initialDraft?: string | null;
  onInitialDraftConsumed?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const isDemo = conversationId.startsWith("demo-");
  const demoConvData = useMemo(() => {
    if (!isDemo) return undefined;
    const found = getDemoChatConversations(i18n.language).find(
      (c) => c.id === conversationId,
    );
    if (!found) return undefined;
    return {
      conversation: {
        id: found.id,
        workspaceId: found.workspaceId,
        title: found.title,
        lastMessageAt: found.lastMessageAt,
        createdAt: found.createdAt,
      } as ChatConversation,
      messages: found.messages as ChatMessage[],
    };
  }, [isDemo, conversationId, i18n.language]);
  const { data, isLoading } = useQuery({
    queryKey: ["chat-conversation", conversationId],
    queryFn: () => getChatConversation(conversationId),
    refetchOnWindowFocus: false,
    enabled: !isDemo,
    initialData: demoConvData,
  });
  const conv = data?.conversation;
  const messages = useMemo<ChatMessage[]>(() => data?.messages ?? [], [data]);

  const [streaming, setStreaming] = useState<string>("");
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll on new content.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  // Reset draft + stream when switching conversations.
  useEffect(() => {
    setStreaming("");
    setDraft("");
  }, [conversationId]);

  // Consume a starter prompt once the conversation is open. We apply it
  // after the conversationId-reset effect above so it isn't immediately
  // wiped, and we clear it on the parent so a manual edit isn't undone
  // on re-render.
  useEffect(() => {
    if (initialDraft && initialDraft.trim().length > 0) {
      setDraft(initialDraft);
      onInitialDraftConsumed?.();
    }
    // We intentionally only react to changes of initialDraft; consuming it
    // sets the parent state to null which re-fires this with empty string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDraft]);

  const handleSend = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault();
      const content = draft.trim();
      if (!content || sending) return;
      if (isDemo) {
        toast({ title: t("chat_page.demo_readonly") });
        return;
      }
      setSending(true);
      setStreaming("");

      // Optimistic user bubble — refetch will replace with the canonical row.
      qc.setQueryData(
        ["chat-conversation", conversationId],
        (old: typeof data) => {
          if (!old) return old;
          return {
            ...old,
            messages: [
              ...old.messages,
              {
                id: `optimistic-${Date.now()}`,
                role: "user",
                content,
                meta: null,
                createdAt: new Date().toISOString(),
              },
            ],
          };
        },
      );
      setDraft("");

      try {
        await sendChatMessageStream(conversationId, content, {
          onUserMessage: () => {
            /* canonical user_message will arrive via refetch below */
          },
          onDelta: (t) => setStreaming((prev) => prev + t),
          onDone: async () => {
            setStreaming("");
            await Promise.all([
              qc.invalidateQueries({
                queryKey: ["chat-conversation", conversationId],
              }),
              qc.invalidateQueries({ queryKey: ["chat-conversations"] }),
            ]);
          },
          onError: (err) => {
            setStreaming("");
            toast({
              title: t("chat_page.send_error"),
              description: err.message,
              variant: "destructive",
            });
            void qc.invalidateQueries({
              queryKey: ["chat-conversation", conversationId],
            });
          },
        });
      } finally {
        setSending(false);
      }
    },
    [conversationId, draft, sending, qc, toast, data],
  );

  const handleWorkspaceChange = async (val: string) => {
    try {
      await patchChatConversation(conversationId, {
        workspaceId: val === "__none__" ? null : val,
      });
      await qc.invalidateQueries({
        queryKey: ["chat-conversation", conversationId],
      });
      await qc.invalidateQueries({ queryKey: ["chat-conversations"] });
    } catch (err) {
      toast({
        title: t("chat_page.update_error"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!conv) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        {t("chat_page.conv_not_found")}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header — stacks title above selector on tiny screens to avoid
          truncation; back button visible on mobile so the user can
          return to the conversation list. */}
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-border bg-background/95 backdrop-blur-sm shrink-0">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 -ml-1"
            onClick={onBack}
            aria-label={t("chat_page.back_aria")}
            data-testid="button-chat-back"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
        )}
        <h2
          className="text-sm font-semibold truncate flex-1 min-w-0"
          data-testid="chat-conversation-title"
        >
          {conv.title}
        </h2>
        <Select
          value={conv.workspaceId ?? "__none__"}
          onValueChange={handleWorkspaceChange}
        >
          <SelectTrigger
            className="w-[120px] sm:w-[180px] h-8 text-xs shrink-0"
            data-testid="select-conversation-workspace"
          >
            <SelectValue placeholder={t("chat_page.no_memory")} />
          </SelectTrigger>
          <SelectContent align="end">
            <SelectItem value="__none__">{t("chat_page.no_memory")}</SelectItem>
            {workspaces.map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 py-4 space-y-3">
        {messages.length === 0 && !streaming && (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            <Sparkles className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p>{t("chat_page.empty")}</p>
            {conv.workspaceId && (
              <p className="mt-1 text-xs">
                {t("chat_page.workspace_memory_label")}
              </p>
            )}
          </Card>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} msg={m} />
        ))}
        {streaming && (
          <MessageBubble
            msg={{
              id: "streaming",
              role: "assistant",
              content: streaming,
              meta: null,
              createdAt: new Date().toISOString(),
            }}
          />
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={handleSend}
        className="border-t border-border p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] flex items-end gap-2 bg-background shrink-0"
      >
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSend();
            }
          }}
          placeholder={t("chat_page.composer_placeholder")}
          rows={1}
          className="min-h-[40px] max-h-[160px] resize-none flex-1 min-w-0"
          disabled={sending}
          data-testid="input-chat-message"
        />
        <Button
          type="submit"
          size="icon"
          className="shrink-0"
          disabled={sending || draft.trim().length === 0}
          data-testid="button-send-chat"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </Button>
      </form>
    </div>
  );
}

// ============================================================
// Landing (empty state) — recent conversations + starter chips
// ============================================================

interface StarterPrompt {
  /** Short label rendered on the chip. */
  label: string;
  /** Full prompt that pre-fills the composer once a conversation opens. */
  prompt: string;
  Icon: typeof Mail;
}

function getStarterPrompts(t: (key: string) => string): StarterPrompt[] {
  return [
    { label: t("chat_page.prompts.mail_summary"), prompt: t("chat_page.prompts.mail_summary_prompt"), Icon: Mail },
    { label: t("chat_page.prompts.projects_at_risk"), prompt: t("chat_page.prompts.projects_at_risk_prompt"), Icon: TrendingDown },
    { label: t("chat_page.prompts.prioritize_day"), prompt: t("chat_page.prompts.prioritize_day_prompt"), Icon: ListChecks },
    { label: t("chat_page.prompts.weekly_review"), prompt: t("chat_page.prompts.weekly_review_prompt"), Icon: CalendarCheck },
    { label: t("chat_page.prompts.brainstorm"), prompt: t("chat_page.prompts.brainstorm_prompt"), Icon: Lightbulb },
    { label: t("chat_page.prompts.write_email"), prompt: t("chat_page.prompts.write_email_prompt"), Icon: PenLine },
  ];
}

function StarterChips({
  onPick,
  disabled,
}: {
  onPick: (prompt: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const prompts = getStarterPrompts(t);
  return (
    <div data-testid="chat-starter-chips" className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
        {t("chat_page.to_start_label")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {prompts.map((p) => (
          <button
            type="button"
            key={p.label}
            onClick={() => onPick(p.prompt)}
            disabled={disabled}
            className="group flex items-center gap-2.5 text-left rounded-xl border border-border/60 bg-card hover:bg-secondary/60 hover:border-border transition-colors px-3 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`chat-starter-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary shrink-0">
              <p.Icon className="w-3.5 h-3.5" />
            </span>
            <span className="text-sm flex-1 min-w-0 truncate">
              {p.label}
            </span>
            <ArrowRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

function RecentConversations({
  conversations,
  onSelect,
  max = 3,
}: {
  conversations: ChatConversation[];
  onSelect: (id: string) => void;
  max?: number;
}) {
  const { t, i18n } = useTranslation();
  const recent = useMemo(
    () =>
      [...conversations]
        .sort(
          (a, b) =>
            new Date(b.lastMessageAt).getTime() -
            new Date(a.lastMessageAt).getTime(),
        )
        .slice(0, max),
    [conversations, max],
  );

  if (recent.length === 0) return null;

  return (
    <div data-testid="chat-recent-conversations" className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
        {t("chat_page.recent_label")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {recent.map((c) => (
          <button
            type="button"
            key={c.id}
            onClick={() => onSelect(c.id)}
            className="group flex flex-col items-start text-left rounded-xl border border-border/60 bg-card hover:bg-secondary/60 hover:border-border transition-colors px-3 py-2.5 min-h-[88px]"
            data-testid={`chat-recent-${c.id}`}
          >
            <div className="flex items-center gap-1.5 w-full">
              <MessageSquare className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="text-sm font-medium flex-1 min-w-0 truncate">
                {c.title}
              </span>
            </div>
            {c.lastMessagePreview ? (
              <p
                className="text-xs text-muted-foreground mt-1 line-clamp-2 w-full"
                data-testid={`chat-recent-preview-${c.id}`}
              >
                {c.lastMessagePreview}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground/70 italic mt-1">
                {t("chat_page.no_message_yet")}
              </p>
            )}
            <span className="text-[10px] text-muted-foreground/80 mt-1.5">
              {formatRelative(c.lastMessageAt, i18n.language)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatLandingEmpty({
  conversations,
  onPickPrompt,
  onSelectConversation,
  pickingPrompt,
  intro,
}: {
  conversations: ChatConversation[];
  onPickPrompt: (prompt: string) => void;
  onSelectConversation: (id: string) => void;
  pickingPrompt: boolean;
  /** Optional override for the headline/lead block. */
  intro?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="w-full max-w-3xl mx-auto space-y-6"
      data-testid="chat-landing-empty"
    >
      {intro ?? (
        <div className="text-center pt-2">
          <Sparkles className="w-8 h-8 mx-auto mb-2 text-primary" />
          <h3 className="text-base font-semibold text-foreground">
            {t("chat_page.landing_title")}
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mt-1">
            {t("chat_page.landing_desc")}
          </p>
        </div>
      )}
      <RecentConversations
        conversations={conversations}
        onSelect={onSelectConversation}
      />
      <StarterChips onPick={onPickPrompt} disabled={pickingPrompt} />
    </div>
  );
}

// ============================================================
// Page
// ============================================================

export default function ChatPage() {
  const { t, i18n } = useTranslation();
  const params = useParams<{ id?: string }>();
  const [, setLocation] = useLocation();
  const conversationId = params.id ?? null;
  const isMobile = useIsMobile();
  const { active: isDemo } = useDemoMode();

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<
    string | null
  >(null);

  const [pendingDraft, setPendingDraft] = useState<string | null>(null);
  const [pickingPrompt, setPickingPrompt] = useState(false);
  const [quickInput, setQuickInput] = useState("");
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: workspacesData } = useQuery({
    queryKey: ["chat-workspaces"],
    queryFn: () => listChatWorkspaces(),
    refetchOnWindowFocus: false,
    enabled: !isDemo,
  });
  const workspaces = isDemo ? [] : (workspacesData?.workspaces ?? []);

  // Conversations list — needed to power the "Reprendre" cards on the
  // empty state. The middle rail uses the same query key so this is
  // already cached when present.
  const { data: conversationsData } = useQuery({
    queryKey: ["chat-conversations"],
    queryFn: () => listChatConversations(),
    refetchOnWindowFocus: false,
    enabled: !isDemo,
  });
  const demoConversations = useMemo(
    () => (isDemo ? (getDemoChatConversations(i18n.language) as ChatConversation[]) : []),
    [isDemo, i18n.language],
  );
  const conversations = isDemo ? demoConversations : (conversationsData?.conversations ?? []);

  const handleSelectConversation = (id: string) => {
    if (id) setLocation(`/app/chat/${id}`);
    else setLocation("/app/chat");
  };

  const handlePickStarterPrompt = useCallback(
    async (prompt: string) => {
      if (pickingPrompt) return;
      if (isDemo) {
        toast({ title: t("chat_page.demo_readonly") });
        return;
      }
      setPickingPrompt(true);
      try {
        const r = await createChatConversation({
          workspaceId: selectedWorkspaceId ?? null,
        });
        await qc.invalidateQueries({ queryKey: ["chat-conversations"] });
        setPendingDraft(prompt);
        handleSelectConversation(r.conversation.id);
      } catch (err) {
        toast({
          title: t("chat_page.create_error"),
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
      } finally {
        setPickingPrompt(false);
      }
    },
    // handleSelectConversation/setLocation are stable (wouter); no need
    // to include them. qc/toast are stable from their respective hooks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pickingPrompt, selectedWorkspaceId, isDemo],
  );

  // Height note: parent <main> in app-shell is `flex-1 overflow-auto`
  // with NO padding. Mobile header (md:hidden) above main is sticky and
  // ~60px tall (py-3 + h-9 avatar). Desktop has no top header.
  // We set explicit dvh-based heights so the chat fills the viewport
  // without horizontal margins (which previously caused content to
  // bleed off-screen).
  return (
    <div className="flex flex-col w-full h-full overflow-hidden bg-background">
      {/* ── v2 announcement banner ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 shrink-0">
        <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
        <p className="text-xs text-amber-800 dark:text-amber-200 flex-1">
          <span className="font-semibold uppercase tracking-wide text-[10px] bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded px-1.5 py-0.5 mr-2">
            {t("chat_page.v2_badge")}
          </span>
          {t("chat_page.v2_desc")}
        </p>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden bg-background pointer-events-none select-none opacity-50">
      {!isMobile && (
        <aside className="flex flex-col w-64 border-r border-border bg-card/30 shrink-0 overflow-hidden">
          <div className="flex items-center gap-2 px-3 pt-3 pb-2 shrink-0">
            <MessageSquare className="w-5 h-5 text-primary" />
            <h2 className="text-sm font-semibold">Chat IA</h2>
          </div>
          <ScrollArea className="flex-1">
            <div className="px-3 pb-3 space-y-4">
              <WorkspacesPanel
                selectedId={selectedWorkspaceId}
                onSelect={setSelectedWorkspaceId}
              />
              {selectedWorkspaceId && (
                <FilesPanel workspaceId={selectedWorkspaceId} />
              )}
              <div className="border-t border-border pt-4">
                <ConversationsPanel
                  selectedId={conversationId}
                  onSelect={handleSelectConversation}
                  workspaceFilter={null}
                  defaultWorkspaceId={selectedWorkspaceId}
                />
              </div>
            </div>
          </ScrollArea>
        </aside>
      )}

      {/* Right pane: active conversation or empty/mobile fallback */}
      <section className="flex-1 min-w-0 flex flex-col h-full">
        {/* Mobile-only Chat IA top bar — only when no conversation is
            open (when one IS open, ConversationView shows its own
            header with a back button). */}
        {isMobile && !conversationId && (
          <div className="border-b border-border px-4 py-3 flex items-center gap-2 shrink-0">
            <MessageSquare className="w-5 h-5 text-primary" />
            <h2 className="text-sm font-semibold flex-1">{t("chat_page.chat_ai_label")}</h2>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={async () => {
                const r = await createChatConversation({
                  workspaceId: selectedWorkspaceId ?? null,
                });
                handleSelectConversation(r.conversation.id);
              }}
              data-testid="button-new-conversation-mobile"
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs">{t("chat_page.new_mobile_btn")}</span>
            </Button>
          </div>
        )}

        {conversationId ? (
          <ConversationView
            conversationId={conversationId}
            workspaces={workspaces}
            onBack={isMobile ? () => setLocation("/app/chat") : undefined}
            initialDraft={pendingDraft}
            onInitialDraftConsumed={() => setPendingDraft(null)}
          />
        ) : isMobile ? (
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-6">
            <ChatLandingEmpty
              conversations={conversations}
              onPickPrompt={handlePickStarterPrompt}
              onSelectConversation={handleSelectConversation}
              pickingPrompt={pickingPrompt}
              intro={
                <div className="text-center">
                  <Sparkles className="w-7 h-7 mx-auto mb-1.5 text-primary" />
                  <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                    {t("chat_page.resume_hint")}
                  </p>
                </div>
              }
            />
            <div>
              <WorkspacesPanel
                selectedId={selectedWorkspaceId}
                onSelect={setSelectedWorkspaceId}
              />
              {selectedWorkspaceId && (
                <FilesPanel workspaceId={selectedWorkspaceId} />
              )}
            </div>
            <ConversationsPanel
              selectedId={null}
              onSelect={handleSelectConversation}
              workspaceFilter={null}
              defaultWorkspaceId={selectedWorkspaceId}
            />
            {workspaces.length === 0 && (
              <Card className="p-4 text-sm text-muted-foreground">
                {t("chat_page.memory_tip")}
              </Card>
            )}
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="flex-1 flex items-center justify-center overflow-y-auto px-8 py-10">
              <ChatLandingEmpty
                conversations={conversations}
                onPickPrompt={handlePickStarterPrompt}
                onSelectConversation={handleSelectConversation}
                pickingPrompt={pickingPrompt}
              />
            </div>
            {/* Quick-start input */}
            <div className="border-t border-border px-4 py-3 shrink-0">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const msg = quickInput.trim();
                  if (!msg || pickingPrompt) return;
                  setQuickInput("");
                  handlePickStarterPrompt(msg);
                }}
                className="flex items-end gap-2"
              >
                <Textarea
                  value={quickInput}
                  onChange={(e) => setQuickInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      const msg = quickInput.trim();
                      if (!msg || pickingPrompt) return;
                      setQuickInput("");
                      handlePickStarterPrompt(msg);
                    }
                  }}
                  placeholder={t("chat_page.prompts.quick_input_placeholder")}
                  className="flex-1 resize-none min-h-[40px] max-h-32 text-sm"
                  rows={1}
                  disabled={pickingPrompt}
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!quickInput.trim() || pickingPrompt}
                >
                  {pickingPrompt
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Send className="w-4 h-4" />}
                </Button>
              </form>
            </div>
          </div>
        )}
      </section>
      </div>
    </div>
  );
}

